/** The AI change a history row belongs to (backend metaplan A3c).
 *
 *  Every row a change set writes carries its id as the correlation id, and the three history
 *  endpoints return it as `change_set` (the generic `/changes/`, `/notification_changes/` and a
 *  tenant's `config_changes/`). Each History tab marks such a row with this chip — "AI change ·
 *  MCP", "Design import", "Revert", … plus a hint once it was reverted — so an edit made by an AI
 *  client never reads like an operator's.
 *
 *  With `console_ai_changes_view` the chip opens the change set in a NEW tab: the History tabs
 *  sit inside editors whose drafts are expensive to lose. Without it, a plain chip that says
 *  what it is. The backend still decides who may open the page. */
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import { Chip, Tooltip, type SxProps, type Theme } from '@mui/material';
import type { MouseEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { shortId, statusLabel, type AIChangeRef } from '@/api/aiChanges';
import { usePermissions } from '@/hooks/usePermissions';

export const AI_CHANGES_VIEW_PERM = 'console_ai_changes_view';

const CHANNEL_WORDS: Record<AIChangeRef['channel'], string> = {
  mcp: 'AI change · MCP',
  chat: 'AI change · Console agent',
  design_import: 'Design import',
  translation_sync: 'Translation sync',
  revert: 'Revert',
};

/** "AI change · MCP", "Design import · reverted", … */
export function changeSetChipLabel(ref: AIChangeRef): string {
  const base = CHANNEL_WORDS[ref.channel] ?? `AI change · ${ref.channel}`;
  if (ref.status === 'reverted') return `${base} · reverted`;
  if (ref.status === 'partially_reverted') return `${base} · partly reverted`;
  return base;
}

interface Props {
  changeSet: AIChangeRef | null | undefined;
  sx?: SxProps<Theme>;
}

/** Nothing for a row no change set wrote — the permission is asked only for one that has. */
export function ChangeSetChip({ changeSet, sx }: Props) {
  return changeSet ? <ChangeSetChipFor changeSet={changeSet} sx={sx} /> : null;
}

function ChangeSetChipFor({ changeSet, sx }: Props & { changeSet: AIChangeRef }) {
  const { hasPerm } = usePermissions();

  const label = changeSetChipLabel(changeSet);
  const what = `Written by change set ${shortId(changeSet.id)} (${statusLabel(changeSet.status).toLowerCase()})`;
  const common = {
    size: 'small' as const,
    variant: 'outlined' as const,
    icon: <PublishedWithChangesIcon />,
    label,
    sx: [{ maxWidth: '100%' }, ...(Array.isArray(sx) ? sx : [sx])],
    'data-change-set-chip': changeSet.id,
  };

  if (hasPerm(AI_CHANGES_VIEW_PERM)) {
    return (
      <Tooltip describeChild title={`${what} — opens in a new tab`}>
        <Chip
          {...common}
          clickable
          component={RouterLink}
          to={`/ai-changes/${encodeURIComponent(changeSet.id)}`}
          target="_blank"
          rel="noopener noreferrer"
          // The rows it sits in expand or select on click; the chip only opens its change set.
          onClick={(e: MouseEvent) => e.stopPropagation()}
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip
      describeChild
      title={`${what}. Opening AI changes needs the console_ai_changes_view permission.`}
    >
      <Chip {...common} />
    </Tooltip>
  );
}
